import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './VerifyEmailPending.css';

const VerifyEmailPending: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const email = searchParams.get('email') || '';

  const handleResendVerification = async () => {
    if (!email) {
      setMessage({ type: 'error', text: 'Email address not found' });
      return;
    }

    setResending(true);
    setMessage(null);

    try {
      const result = await apiService.resendVerification(email);

      if (result.status === 'success') {
        setMessage({ type: 'success', text: result.message });
      } else {
        throw new Error(result.detail || 'Failed to resend verification email');
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to resend verification email'
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-pending-page">
      <div className="verify-container">
        <div className="verify-icon">
          📧
        </div>
        
        <h1>Check Your Email</h1>
        
        <div className="email-display">
          <strong>{email}</strong>
        </div>
        
        <p className="verify-description">
          We've sent a verification email to the address above.
          Click the verification link in the email to activate your account and get access to <strong>3 free reports</strong>.
        </p>

        <div className="verification-benefits">
          <div className="benefit-item">
            <span className="benefit-icon">📊</span>
            <span>3 Free Reports</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">⚡</span>
            <span>Instant Activation</span>
          </div>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✗'} {message.text}
          </div>
        )}

        <div className="verify-actions">
          <button 
            onClick={handleResendVerification}
            disabled={resending}
            className="btn secondary resend-btn"
          >
            {resending ? 'Resending...' : 'Resend Verification Email'}
          </button>
          
          <button 
            onClick={() => navigate('/pricing')}
            className="btn tertiary"
          >
            Back to Pricing
          </button>
        </div>

        <div className="verify-help">
          <h4>Didn't receive the email?</h4>
          <ul>
            <li>Check your spam/junk folder</li>
            <li>Make sure you entered the correct email address</li>
            <li>Wait a few minutes - emails can take time to arrive</li>
            <li>Click "Resend" if you still don't see it</li>
          </ul>
          
          <div className="support-contact">
            <p>Still having trouble? Contact our support team:</p>
            <a href="mailto:info@use-proven.com" className="support-link">
              📧 info@use-proven.com
            </a>
          </div>
        </div>

        <div className="security-note">
          <span className="security-icon">🔒</span>
          <p>
            We verify all email addresses to ensure platform security and prevent abuse. 
            Your verification link expires in 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPending;